/* eslint-disable no-console */
import * as Sentry from "@sentry/browser";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState, useMemo } from "react";
import { shouldAutoReadContent } from "../../helpers/content-reader-helper";
import ScrollButton from "../scroll-button/scroll-button";
import SubHeader from "../sub-header/sub-header";
import "./content-reader.scss";
import getReaderConfig from "./reader-config";
import DictionaryEnum from "../../enums/dictionary.enum";
window.ContentReader = window.ContentReader || {};
function ContentReader(props) {
    var token = props.token, doolittleBaseMediaUrl = props.doolittleBaseMediaUrl, doolittleBaseApiUrl = props.doolittleBaseApiUrl, doolittleVersion = props.doolittleVersion, displayCorrectAnswer = props.displayCorrectAnswer, debug = props.debug, disableFastForward = props.disableFastForward, theme = props.theme, isSpanishContent = props.isSpanishContent, _a = props.disableAnnotations, disableAnnotations = _a === void 0 ? true : _a, _b = props.shouldAutoPlay, shouldAutoPlay = _b === void 0 ? true : _b, _c = props.isAudioCompletionRequired, isAudioCompletionRequired = _c === void 0 ? false : _c, appEnv = props.appEnv, itemEngineAuthorUri = props.itemEngineAuthorUri, itemEngineItemsUri = props.itemEngineItemsUri, _d = props.speechStreamEnabled, speechStreamEnabled = _d === void 0 ? true : _d;
    var _e = useState({ loaded: false, error: false }), scriptState = _e[0], setScriptState = _e[1];
    var _f = useState({ loaded: false, error: false }), ttsScriptState = _f[0], setTtsScriptState = _f[1];
    var _g = useState({ display: false, subtitle: "", icon: "" }), navSubHead = _g[0], setNavSubHead = _g[1];
    var scrollIndicatorRef = useRef(null);
    var contentReaderRef = useRef(null);
    var apiUrl = "".concat(doolittleBaseApiUrl, "/api/gw");
    var uri = "".concat(apiUrl, "/graphql");
    var debugMode = !!debug;
    var noFastForward = !!disableFastForward;
    var uxTheme = "student-".concat(theme || "3-5");
    var contentReaderWrapperTag = "content-reader-wrapper";
    var contentReaderTag = "content-reader";
    var navSubActiveClass = navSubHead.display ? "nav-active" : "";
    var richTextType = disableAnnotations ? "ModifiedRichText" : "RichText";
    var minimumVideoQualityDefault = 270;
    var minimumVideoQualityOverride = sessionStorage.getItem("minimumVideoQuality");
    var minimumVideoQuality = minimumVideoQualityOverride === null || Number.isNaN(Number(minimumVideoQualityOverride))
        ? minimumVideoQualityDefault
        : Number(minimumVideoQualityOverride);
    var _h = useMemo(function () {
        var dictionary = DictionaryEnum.MWLEARNERS;
        if (theme === "6-8") {
            dictionary = DictionaryEnum.MWINTERMEDIATE;
        }
        else if (theme === "9-12") {
            dictionary = DictionaryEnum.MWSCHOOL;
        }
        return { shouldAutoRead: shouldAutoReadContent(theme), dictionaryType: dictionary };
    }, [theme]), shouldAutoRead = _h.shouldAutoRead, dictionaryType = _h.dictionaryType;
    var launchSettings = useMemo(function () { return ({
        featureFlags: appEnv
            ? {
                entityContext: {
                    product: "MPNG",
                    env: appEnv
                }
            }
            : null,
        contentComponents: {
            Question: {
                revealAnswerCorrectness: !!displayCorrectAnswer
            },
            QuestionItem: {
                revealAnswerCorrectness: !!displayCorrectAnswer
            }
        },
        mathJax: {
            script: "https://cdn.jsdelivr.net/npm/mathjax@3/es5",
            config: {
                loader: { load: ["a11y/semantic-enrich", "a11y/explorer"] },
                options: {
                    sre: {
                        speech: "shallow" // one of: 'deep', 'shallow', or 'none'
                    },
                    renderActions: {
                        //
                        // Force speech enrichment regardless of the menu settings
                        //
                        enrich: {
                            "[+]": [
                                function (doc) {
                                    doc.enrich(true);
                                },
                                function (math, doc) {
                                    math.enrich(doc, true);
                                }
                            ]
                        }
                    }
                },
                tex: {
                    inlineMath: [
                        ["$$", "$$"],
                        ["$", "$"],
                        ["\\(", "\\)"]
                    ]
                }
            }
        }
    }); }, [displayCorrectAnswer]);
    var readerConfig = useMemo(function () {
        return getReaderConfig(debugMode, apiUrl, disableAnnotations, !!isSpanishContent, richTextType, doolittleBaseMediaUrl, noFastForward, minimumVideoQuality, shouldAutoRead, shouldAutoPlay, isAudioCompletionRequired, dictionaryType, itemEngineAuthorUri, itemEngineItemsUri, speechStreamEnabled);
    }, [
        apiUrl,
        debugMode,
        dictionaryType,
        disableAnnotations,
        doolittleBaseMediaUrl,
        isAudioCompletionRequired,
        isSpanishContent,
        minimumVideoQuality,
        noFastForward,
        richTextType,
        shouldAutoPlay,
        shouldAutoRead,
        itemEngineAuthorUri,
        itemEngineItemsUri,
        speechStreamEnabled
    ]);
    var scrollCallBack = function () {
        var scrollIndicatorDiv = scrollIndicatorRef.current;
        if (scrollIndicatorDiv)
            scrollIndicatorDiv.style.display = "none";
    };
    var showHideScrollIndicator = useCallback(function () {
        var playerDiv = document.getElementById(contentReaderWrapperTag);
        var scrollIndicatorDiv = scrollIndicatorRef.current;
        if (playerDiv !== null && scrollIndicatorDiv) {
            setTimeout(function () {
                scrollIndicatorDiv.style.display = playerDiv.scrollHeight > playerDiv.clientHeight ? "block" : "none";
            }, 100);
            playerDiv.removeEventListener("scroll", scrollCallBack);
            playerDiv.addEventListener("scroll", scrollCallBack, { once: true });
        }
    }, []);
    var execEventFunctionIfExist = useCallback(function (eventFunction, data) {
        if (typeof eventFunction === "function")
            eventFunction(data);
    }, []);
    var renderSubHead = function () {
        var _a, _b, _c;
        try {
            var isContentLayout = !!document.querySelector(".wnr-contentLayout-layout");
            var withIcon = document.querySelector(".wnr-contentLayout-layout-type-subhead_with_icon");
            var withoutIcon = document.querySelector(".wnr-contentLayout-layout-type-subhead_without_icon");
            var isSubheadWithOrWithoutIconContentLayout = withIcon || withoutIcon;
            if (isContentLayout && isSubheadWithOrWithoutIconContentLayout) {
                var subheadContainer = isSubheadWithOrWithoutIconContentLayout.querySelector(".wnr-contentLayout-empty-bar + div");
                if (!subheadContainer)
                    throw new Error("SubHeadLayout - Layout format incompatible");
                var subtitle = ((_a = subheadContainer.querySelector("h3 span span")) === null || _a === void 0 ? void 0 : _a.textContent) ||
                    ((_b = subheadContainer.querySelector(".wnr-cctext-richText-richtext-editor span span")) === null || _b === void 0 ? void 0 : _b.textContent);
                if (!subtitle) {
                    isSubheadWithOrWithoutIconContentLayout === null || isSubheadWithOrWithoutIconContentLayout === void 0 ? void 0 : isSubheadWithOrWithoutIconContentLayout.classList.add("loaded");
                    throw new Error("SubHeadLayout - Subtitle not found");
                }
                var icon = (_c = subheadContainer.querySelector(".wnr-ccimg-image-container img")) === null || _c === void 0 ? void 0 : _c.getAttribute("src");
                if (withIcon && !icon) {
                    isSubheadWithOrWithoutIconContentLayout === null || isSubheadWithOrWithoutIconContentLayout === void 0 ? void 0 : isSubheadWithOrWithoutIconContentLayout.classList.add("loaded");
                    throw new Error("SubHeadLayout - Image not found");
                }
                subheadContainer.classList.add("hide-duplicate-header");
                setNavSubHead({ display: true, subtitle: subtitle || "", icon: icon || "" });
                isSubheadWithOrWithoutIconContentLayout === null || isSubheadWithOrWithoutIconContentLayout === void 0 ? void 0 : isSubheadWithOrWithoutIconContentLayout.classList.add("loaded");
            }
        }
        catch (err) {
            console.error(err.message);
        }
    };
    // function to increase the container width if the video leaded is small
    var renderContainerWidth = function () {
        try {
            var videoElement = document.querySelector("video.vjs-tech");
            var videoContainer = document.querySelector(".wnr-ccmedia-media-media-container .video-js");
            var videoContainerLoaded = document.querySelector(".wnr-ccmedia-media-video");
            if (videoContainer !== null &&
                videoElement !== null &&
                videoContainerLoaded !== null &&
                videoElement.getAttribute("src") !== null &&
                !videoContainer.classList.contains("video-container-resize") &&
                !videoContainer.classList.contains("video-with-transcripts")) {
                videoContainer.classList.remove("video-container-resize");
                var vh = window.innerHeight;
                var vw = window.innerWidth;
                if (vw > vh) {
                    var videoHeight = videoElement.getBoundingClientRect().height;
                    var svRatio = videoHeight / vh; // video to screen ratio
                    if ((vh > 768 && svRatio < 0.75) || (vh <= 768 && svRatio < 0.74)) {
                        videoContainer.classList.add("video-container-resize");
                    }
                }
            }
        }
        catch (e) {
            console.error(e.message);
        }
    };
    // Callback function to execute when mutations on the transcripts are observed
    var transcriptObserverCallback = function (mutationsList) {
        for (var i = 0, len = mutationsList.length; i < len; i += 1) {
            if (mutationsList[i].type === "childList") {
                var tSection = document.getElementsByClassName("wnr-ccmedia-transcript-transcript-read-mode")[0];
                var segmentLayout = document.getElementsByClassName("wnr-segment-layout")[0];
                if (tSection !== undefined) {
                    // transcripts section is loaded
                    if (segmentLayout !== undefined) {
                        segmentLayout.classList.add("video-with-transcripts");
                    }
                }
                else if (segmentLayout !== undefined) {
                    segmentLayout.classList.remove("video-with-transcripts");
                }
            }
        }
    };
    // Create an observer instance linked to the transcripts callback function
    var transcriptObserver = useMemo(function () { return new MutationObserver(transcriptObserverCallback); }, []);
    var renderDynamicWidth = useCallback(function () {
        var _a;
        try {
            // the transcripts section parent node
            var targetNode = document.getElementsByClassName("wnr-ccmedia-transcript-transcript-form")[0];
            var parentLayout = document.getElementsByClassName("wnr-contentLayout-layout-type-video")[0];
            // remove transcripts width class to prevent next video from being oversized
            (_a = document.querySelector(".video-with-transcripts")) === null || _a === void 0 ? void 0 : _a.classList.remove("video-with-transcripts");
            if (targetNode && parentLayout && transcriptObserver) {
                transcriptObserver.observe(targetNode, { childList: true });
            }
        }
        catch (e) {
            console.error(e.message);
        }
    }, [transcriptObserver]);
    var logToConsole = useCallback(function (message) {
        if (debugMode) {
            console.log(message);
        }
    }, [debugMode]);
    var subscribeToReaderEvents = useCallback(function (reader) {
        reader.addListener("launched", function (data) {
            logToConsole("reader - launched");
            execEventFunctionIfExist(window.ContentReader.onPathLaunched, data);
        });
        reader.addListener("segmentComplete", function (data) {
            logToConsole("reader - segment complete");
            execEventFunctionIfExist(window.ContentReader.onSegmentComplete, data);
        });
        reader.addListener("segmentStateChange", function (data) {
            var _a;
            logToConsole("reader - segment state change");
            // remove close button from feedback videos
            if (document.querySelector(".wnr-ccquestion-distractor-button") &&
                document.querySelector(".wnr-ccmedia-media-video"))
                (_a = document.querySelector(".wnr-ccquestion-distractor-button")) === null || _a === void 0 ? void 0 : _a.remove();
            execEventFunctionIfExist(window.ContentReader.onSegmentStateChange, data);
            renderContainerWidth();
        });
        reader.addListener("takeCompletionStateChange", function (data) {
            logToConsole("reader - take completion state change");
            execEventFunctionIfExist(window.ContentReader.onTakeCompletionStateChange, data);
        });
        reader.addListener("segmentNavigationChange", function (data) {
            logToConsole("reader - segment navigation change");
            execEventFunctionIfExist(window.ContentReader.onSegmentNavigationChange, data);
        });
        reader.addListener("takeComplete", function (data) {
            logToConsole("reader - take complete");
            execEventFunctionIfExist(window.ContentReader.onTakeComplete, data);
        });
        reader.addListener("takeSubmitComplete", function (data) {
            logToConsole("reader - take submit complete");
            execEventFunctionIfExist(window.ContentReader.onTakeSubmitComplete, data);
        });
        reader.addListener("takeStateChange", function (data) {
            logToConsole("reader - take state change");
            execEventFunctionIfExist(window.ContentReader.onTakeStateChange, data);
        });
        reader.addListener("segmentReady", function (data) {
            logToConsole("reader - segment ready");
            var waitToRenderSubHead = new Promise(function (resolve) {
                renderSubHead();
                resolve("Success!");
            });
            // Changing the width of the video screen when transcripts are loaded
            renderDynamicWidth();
            waitToRenderSubHead.then(function () { return execEventFunctionIfExist(window.ContentReader.onSegmentReady, data); });
            showHideScrollIndicator();
            renderContainerWidth();
        });
    }, [execEventFunctionIfExist, logToConsole, renderDynamicWidth, showHideScrollIndicator]);
    var publishReaderEvents = useCallback(function (reader) {
        window.ContentReader.togglePanel = function (panelId) {
            logToConsole("reader - togglePanel");
            reader.togglePanel(panelId);
        };
        window.ContentReader.goToNextSegment = function () {
            setNavSubHead({ display: false, subtitle: "", icon: "" });
            logToConsole("reader - goToNextSegment");
            reader.goToNextSegment();
        };
        window.ContentReader.goToPrevSegment = function () {
            setNavSubHead({ display: false, subtitle: "", icon: "" });
            logToConsole("reader - goToPrevSegment");
            reader.goToPrevSegment();
        };
        window.ContentReader.goToSegment = function (segmentId) {
            setNavSubHead({ display: false, subtitle: "", icon: "" });
            logToConsole("reader - goToSegment, segmentId: ".concat(segmentId));
            reader.goToSegment(segmentId);
        };
        window.ContentReader.checkAnswers = function () {
            logToConsole("reader - checkAnswers");
            reader.checkAnswers();
        };
        window.ContentReader.submitTake = function () {
            logToConsole("reader - submitTake");
            reader.submitTake();
        };
        window.ContentReader.closeFeedback = function () {
            var _a;
            logToConsole("reader - close feedback");
            (_a = document.querySelectorAll(".sidekick-message")[0]) === null || _a === void 0 ? void 0 : _a.classList.remove("hide-sidekick");
            reader.closeFeedback();
        };
        window.ContentReader.setTranslationLanguage = function (langCode) {
            logToConsole("reader - Set Translation language");
            reader.setTranslationLanguage(langCode);
        };
        window.ContentReader.destroy = function () {
            logToConsole("reader - destroy");
            window.ContentReader.setTranslationLanguage("en");
            reader.destroy();
        };
    }, [logToConsole]);
    var launchReader = useCallback(function () {
        // @ts-ignore
        if (typeof WNReader !== "undefined") {
            logToConsole("WNReader called: ".concat(document.getElementById(contentReaderTag)));
            // @ts-ignore
            var reader = window.WNReader("#".concat(contentReaderTag), readerConfig);
            subscribeToReaderEvents(reader);
            reader.launch({
                uri: uri,
                token: token,
                launchSettings: launchSettings
            });
            publishReaderEvents(reader);
        }
    }, [launchSettings, logToConsole, readerConfig, subscribeToReaderEvents, uri, token, publishReaderEvents]);
    // Doolittle load error message
    var doolittleLoadError = useCallback(function () {
        var wrapperDiv = document.getElementById(contentReaderWrapperTag);
        if (!document.getElementById("dolittle-load-error")) {
            var errorMsg = document.createElement("div");
            errorMsg.id = "dolittle-load-error";
            errorMsg.innerHTML = "Oops! Some Resources could not be downloaded. Please contact your IT support";
            if (wrapperDiv)
                wrapperDiv.appendChild(errorMsg);
            Sentry.captureException(new Error("CTK Reader Resources could not be downloaded. Url - ".concat(doolittleBaseMediaUrl)), function (scope) {
                scope.clear();
                scope.setTags({
                    userId: sessionStorage.getItem("userID"),
                    grade: sessionStorage.getItem("gradeLevel")
                });
                return scope;
            });
        }
    }, [doolittleBaseMediaUrl]);
    var loadReaderStylesheet = useCallback(function () {
        var stylesheetLink = document.createElement("link");
        stylesheetLink.href = "".concat(doolittleBaseMediaUrl, "/static/reader/").concat(doolittleVersion, "/reader.").concat(doolittleVersion, ".css");
        stylesheetLink.rel = "stylesheet";
        stylesheetLink.id = "dolittle-css";
        stylesheetLink.onerror = function () {
            if (debugMode)
                doolittleLoadError();
        };
        document.body.appendChild(stylesheetLink);
        return stylesheetLink;
    }, [debugMode, doolittleBaseMediaUrl, doolittleLoadError, doolittleVersion]);
    var loadReaderJs = useCallback(function () {
        var doolittleIsReady = "doolittle-reader-ready";
        var readerScript = document.getElementById("doolittle-reader.js");
        if (readerScript === null) {
            var script = document.createElement("script");
            script.id = "doolittle-reader.js";
            script.src = "".concat(doolittleBaseMediaUrl, "/static/reader/").concat(doolittleVersion, "/reader.").concat(doolittleVersion, ".js");
            script.async = true;
            script.onload = function () {
                var reader = document.getElementById("doolittle-reader.js");
                if (reader) {
                    reader.className = "ready";
                    document.dispatchEvent(new CustomEvent("doolittle-reader-ready"));
                    setScriptState({ loaded: true, error: false });
                }
            };
            script.onerror = function () {
                doolittleLoadError();
            };
            document.body.appendChild(script);
        }
        else if (readerScript.classList.contains("ready")) {
            // When readerScript loading was initiated by edge-lessons preloader
            setScriptState({ loaded: true, error: false });
        }
        else {
            document.addEventListener(doolittleIsReady, function () { return setScriptState({ loaded: true, error: false }); });
        }
    }, [doolittleBaseMediaUrl, doolittleLoadError, doolittleVersion]);
    var scrollContent = useCallback(function () {
        if (contentReaderRef.current)
            contentReaderRef.current.scrollTop = contentReaderRef.current.scrollHeight;
        var scrollIndicatorDiv = scrollIndicatorRef.current;
        if (scrollIndicatorDiv) {
            scrollIndicatorDiv.style.display = "none";
        }
    }, []);
    useLayoutEffect(function () {
        var stylesheetLink = loadReaderStylesheet();
        loadReaderJs();
        return function () {
            if (debugMode) {
                console.log("reader useLayoutEffect1 cleanup");
            }
            document.body.removeChild(stylesheetLink);
            var node = document.getElementById(contentReaderTag);
            if (node) {
                if (debugMode) {
                    console.log("".concat(contentReaderTag, " is being removed"));
                }
                node.remove();
                if (typeof window.ContentReader.destroy === "function") {
                    if (debugMode) {
                        console.log("reader destroy called in useLayoutEffect1 cleanup");
                    }
                    window.ContentReader.destroy();
                }
            }
        };
    }, [debugMode, loadReaderStylesheet, loadReaderJs]);
    useLayoutEffect(function () {
        if (scriptState.loaded && document.getElementById(contentReaderTag) !== null) {
            launchReader();
            window.addEventListener("resize", showHideScrollIndicator);
        }
        return function () {
            window.removeEventListener("resize", showHideScrollIndicator);
        };
    }, [launchReader, scriptState.loaded, showHideScrollIndicator]);
    useEffect(function () {
        if (ttsScriptState.loaded) {
            window.TexthelpSpeechStream.addToolbar();
        }
    }, [ttsScriptState.loaded]);
    return (React.createElement("div", { id: contentReaderWrapperTag, className: "".concat(contentReaderWrapperTag, " ").concat(navSubActiveClass), ref: contentReaderRef },
        navSubHead.display && React.createElement(SubHeader, { icon: navSubHead.icon, subtitle: navSubHead.subtitle }),
        React.createElement("div", { id: contentReaderTag, className: "".concat(contentReaderTag, " ").concat(uxTheme) }),
        React.createElement("div", { className: "scroll-indicator bounce", ref: scrollIndicatorRef, onClick: scrollContent, onKeyUp: function () { return null; }, role: "button", tabIndex: 0 },
            React.createElement(ScrollButton, { buttonText: props.scrollButtonText }))));
}
export default ContentReader;
